import React, { useState, useEffect, useMemo } from "react";
import {
   Grid,
   Box,
   TextField,
   Button,
   CircularProgress,
   Autocomplete,
   FormControl,
   FormLabel,
   RadioGroup,
   FormControlLabel,
   Radio,
   Typography,
   Divider,
   useMediaQuery,
   useTheme,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import {
   CreateContraVoucher,
   BankDetailList,
   GetContraVoucherNo,
   UpdateContraVoucher,
} from "../../../Services/Services";

const ContraVoucherForm = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // ✅ Mobile detection

   const navigate = useNavigate();
   const location = useLocation();
   const isEditMode = Boolean(location.state?.editMode);
   const editData = location.state?.voucherData || null;

   // Get division_id from localStorage
   const getSelectedDivision = () => {
      try {
         const stored = localStorage.getItem("selectedDivision");
         return stored ? JSON.parse(stored) : null;
      } catch (e) {
         console.error("Failed to parse selectedDivision:", e);
         return null;
      }
   };

   const storedDivision = getSelectedDivision();

   const [loading, setLoading] = useState(false);

   const [formData, setFormData] = useState({
      voucher_no: "",
      date: "",
      voucher_type: "",
      cash_amount: "",
      cheque_no: "",
      cheque_date: "",
      from_account: "",
      to_account: "",
      narration: "",
      division: storedDivision?.division_id || "",
      created_by: localStorage.getItem("user_id"),
   });

   const [bankAccounts, setBankAccounts] = useState([]);
   const [voucherNoLoading, setVoucherNoLoading] = useState(false);

   // Fetch Voucher No (skip in edit mode)
   useEffect(() => {
      if (isEditMode) return;
      const fetchVoucherNo = async () => {
         try {
            setVoucherNoLoading(true);
            const res = await GetContraVoucherNo();
            const voucherNo =
               (res && (res.cv_voucher_no || res.voucher_no || res.number)) ||
               (typeof res === "string" ? res : "");
            if (voucherNo) {
               setFormData((prev) => ({ ...prev, voucher_no: voucherNo }));
            } else toast.error("Failed to fetch voucher number");
         } catch {
            toast.error("Failed to fetch voucher number");
         } finally {
            setVoucherNoLoading(false);
         }
      };
      fetchVoucherNo();
   }, [isEditMode]);

   // Fetch Banks
   useEffect(() => {
      const fetchBanks = async () => {
         const data = await BankDetailList();
         const list = Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data)
               ? data
               : [];
         setBankAccounts(list || []);
      };
      fetchBanks();
   }, []);

   // Prefill in edit mode
   useEffect(() => {
      if (!isEditMode || !editData) return;
      setFormData({
         voucher_no: editData.voucher_no || "",
         date: (editData.date || "").slice(0, 10),
         voucher_type: editData.voucher_type || "",
         cash_amount: editData.cash_amount ?? "",
         cheque_no: editData.cheque_no || "",
         cheque_date: (editData.cheque_date || "").slice(0, 10),
         from_account: editData.from_account || "",
         to_account: editData.to_account || "",
         narration: editData.narration || "",
         division: storedDivision?.division_id || ""
      });
   }, [isEditMode, editData]);

   const voucherTypes = [
      { value: "CASH TO BANK", label: "Cash to Bank" },
      { value: "BANK TO CASH", label: "Bank to Cash" },
      { value: "BANK TO BANK", label: "Bank to Bank" },
   ];

   const isCTB = formData.voucher_type === "CASH TO BANK";
   const isBTC = formData.voucher_type === "BANK TO CASH";
   const isBTB = formData.voucher_type === "BANK TO BANK";

   const handleFormChange = (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
   };

   const handleVoucherTypeChange = (value) => {
      setFormData((prev) => ({
         ...prev,
         voucher_type: value,
         cash_amount: "",
         from_account: "",
         to_account: "",
         created_by: localStorage.getItem("user_id"),
      }));
   };

   const fromBankValue = useMemo(
      () => bankAccounts.find((b) => b.id === formData.from_account) || null,
      [bankAccounts, formData.from_account]
   );
   const toBankValue = useMemo(
      () => bankAccounts.find((b) => b.id === formData.to_account) || null,
      [bankAccounts, formData.to_account]
   );

   const fromBankOptions = useMemo(
      () => bankAccounts.filter((b) => b.id !== formData.to_account),
      [bankAccounts, formData.to_account]
   );
   const toBankOptions = useMemo(
      () => bankAccounts.filter((b) => b.id !== formData.from_account),
      [bankAccounts, formData.from_account]
   );

   const handleSubmit = async () => {
      if (!formData.voucher_no || !formData.date || !formData.voucher_type) {
         toast.error("Please fill Voucher No, Date and Method!");
         return;
      }

      const isCashAmtValid = Number(formData.cash_amount) > 0;

      if (isBTC) {
         if (!formData.from_account)
            return toast.error("Please select bank (From Bank).");
         if (!isCashAmtValid) return toast.error("Please enter valid Cash Amount.");
      }

      if (isCTB) {
         if (!formData.to_account)
            return toast.error("Please select bank (To Bank).");
         if (!isCashAmtValid) return toast.error("Please enter valid Cash Amount.");
      }

      if (isBTB) {
         if (!formData.from_account || !formData.to_account)
            return toast.error("Please select From Bank and To Bank.");
         if (formData.from_account === formData.to_account)
            return toast.error("From and To Bank cannot be same.");
      }

      const postedData = {
         voucher_no: (formData.voucher_no || "").trim(),
         date: formData.date,
         voucher_type: formData.voucher_type,
         cash_amount: Number(formData.cash_amount || 0),
         cheque_no: formData.cheque_no || null,
         cheque_date: formData.cheque_date || null,
         from_account: formData.from_account || null,
         to_account: formData.to_account || null,
         narration: formData.narration?.trim() || null,
         created_by: formData.created_by || null,
         division: storedDivision?.division_id || "",
      };

      try {
         setLoading(true);
         if (isEditMode) {
            const res = await UpdateContraVoucher(editData.cv_id, postedData);
            setLoading(false);
            if (!res?.error) {
               toast.success("Contra Voucher Updated Successfully!");
               setTimeout(() => navigate(-1), 800);
            } else
               toast.error(res?.message || "Failed to update Contra Voucher!");
         } else {
            const res = await CreateContraVoucher(postedData);
            setLoading(false);
            if (!res?.error) {
               toast.success("Contra Voucher Created Successfully!");
               setTimeout(() => navigate(-1), 800);
            } else toast.error(res?.message || "Failed to create Contra Voucher!");
         }
      } catch {
         setLoading(false);
         toast.error("Something went wrong!");
      }
   };

   const bankLabel = (opt) => (opt?.bank_name ? String(opt.bank_name) : "");

   return (
      <>
         {/* ✅ BUTTONS - Desktop same, Mobile stack */}
         {isMobile ? (
            // Mobile View
            <Grid container spacing={2} sx={{ mb: 2, mt: -1, px: 2 }}>
               <Grid item xs={6}>
                  <Button
                     fullWidth
                     variant="outlined"
                     onClick={() => navigate(-1)}
                     sx={{
                        borderRadius: 0,
                        height: 35,
                        fontSize: "0.8rem",
                        textTransform: "none",
                     }}
                  >
                     ← Back
                  </Button>
               </Grid>
               <Grid item xs={6}>
                  <Button
                     fullWidth
                     variant="contained"
                     onClick={handleSubmit}
                     disabled={loading}
                     sx={{
                        borderRadius: 0,
                        height: 35,
                        fontSize: "0.8rem",
                        textTransform: "none",
                     }}
                  >
                     {loading ? (
                        <CircularProgress size={22} color="inherit" />
                     ) : isEditMode ? (
                        "Update"
                     ) : (
                        "Submit"
                     )}
                  </Button>
               </Grid>
            </Grid>
         ) : (
            // Desktop View (Original)
            <Grid
               item
               xs={12}
               sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mb: 2,
                  mt: -1,
               }}
            >
               <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  sx={{
                     borderRadius: 0,
                     height: 35,
                     minWidth: 100,
                     fontSize: "0.8rem",
                     textTransform: "none",
                  }}
               >
                  ← Back
               </Button>
               <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                     borderRadius: 0,
                     height: 35,
                     minWidth: 120,
                     fontSize: "0.8rem",
                     textTransform: "none",
                  }}
               >
                  {loading ? (
                     <CircularProgress size={22} color="inherit" />
                  ) : isEditMode ? (
                     "Update"
                  ) : (
                     "Submit"
                  )}
               </Button>
            </Grid>
         )}

         <ToastContainer position="top-right" autoClose={3000} />

         <Box p={2}>
            <Box border="1px solid #ccc" borderRadius="8px" p={isMobile ? 2 : 3} bgcolor="#fff">
               <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                     <TextField
                        label="Voucher No"
                        size="small"
                        fullWidth
                        required
                        value={formData.voucher_no}
                        onChange={(e) =>
                           handleFormChange("voucher_no", e.target.value)
                        }
                        placeholder={voucherNoLoading ? "Fetching..." : ""}
                        disabled
                     />
                  </Grid>

                  <Grid item xs={12} md={5}>
                     <TextField
                        type="date"
                        label="Date"
                        size="small"
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        value={formData.date}
                        onChange={(e) => handleFormChange("date", e.target.value)}
                     />
                  </Grid>
               </Grid>

               <Divider sx={{ my: 3 }} />

               {/* Method of Transaction */}
               <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                     <FormControl fullWidth>
                        <FormLabel
                           sx={{
                              fontWeight: 600,
                              mb: 1.5,
                              textAlign: "left",
                              display: "block",
                           }}
                        >
                           Method of Transaction *
                        </FormLabel>
                        {/* ✅ Desktop: one line, Mobile: wrap */}
                        <RadioGroup
                           row={!isMobile}
                           value={formData.voucher_type}
                           onChange={(e) => handleVoucherTypeChange(e.target.value)}
                           sx={{ gap: isMobile ? 1 : 3, flexWrap: isMobile ? "wrap" : "nowrap" }}
                        >
                           {voucherTypes.map((v) => (
                              <FormControlLabel
                                 key={v.value}
                                 value={v.value}
                                 control={<Radio size="small" />}
                                 label={v.label}
                                 sx={{ m: 0 }}
                              />
                           ))}
                        </RadioGroup>
                        {!formData.voucher_type && (
                           <Typography variant="caption" color="error">
                              Please select a transaction method
                           </Typography>
                        )}
                     </FormControl>
                  </Grid>

                  {/* BANK_TO_CASH */}
                  {isBTC && (
                     <>
                        <Grid item xs={12} md={3}>
                           <Autocomplete
                              options={fromBankOptions || []}
                              value={fromBankValue}
                              getOptionLabel={bankLabel}
                              renderOption={(props, option) => (
                                 <li {...props}>
                                    {option.bank_name} — {option.account_number}
                                 </li>
                              )}
                              onChange={(e, val) =>
                                 handleFormChange("from_account", val ? val.id : "")
                              }
                              renderInput={(params) => (
                                 <TextField
                                    {...params}
                                    label="Select Bank"
                                    size="small"
                                    fullWidth
                                 />
                              )}
                              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                           />
                        </Grid>

                        <Grid item xs={12} md={3}>
                           <TextField
                              label="Cheque No"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.cheque_no}
                              onChange={(e) =>
                                 handleFormChange("cheque_no", e.target.value)
                              }
                              onWheel={(e) => e.target.blur()}
                              inputProps={{ min: 0, step: "0.01" }}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              label="Cash Amt"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.cash_amount}
                              onChange={(e) =>
                                 handleFormChange("cash_amount", e.target.value)
                              }
                              onWheel={(e) => e.target.blur()}
                              inputProps={{ min: 0, step: "0.01" }}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              label="Date of Cheque"
                              type="date"
                              size="small"
                              fullWidth
                              value={formData.cheque_date}
                              onChange={(e) =>
                                 handleFormChange("cheque_date", e.target.value)
                              }
                              onWheel={(e) => e.target.blur()}
                              InputLabelProps={{ shrink: true }}
                           />
                        </Grid>
                     </>
                  )}

                  {/* CASH_TO_BANK */}
                  {isCTB && (
                     <>
                        <Grid item xs={12} md={4}>
                           <TextField
                              label="Cash Amt"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.cash_amount}
                              onChange={(e) =>
                                 handleFormChange("cash_amount", e.target.value)
                              }
                              onWheel={(e) => e.target.blur()}
                              inputProps={{ min: 0, step: "0.01" }}
                           />
                        </Grid>

                        <Grid item xs={12} md={4}>
                           <Autocomplete
                              options={toBankOptions || []}
                              value={toBankValue}
                              getOptionLabel={bankLabel}
                              renderOption={(props, option) => (
                                 <li {...props}>
                                    {option.bank_name} — {option.account_number}
                                 </li>
                              )}
                              onChange={(e, val) =>
                                 handleFormChange("to_account", val ? val.id : "")
                              }
                              renderInput={(params) => (
                                 <TextField
                                    {...params}
                                    label="Select Bank"
                                    size="small"
                                    fullWidth
                                 />
                              )}
                              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                           />
                        </Grid>
                     </>
                  )}

                  {/* BANK_TO_BANK */}
                  {isBTB && (
                     <>
                        <Grid item xs={12} md={3}>
                           <Autocomplete
                              options={fromBankOptions || []}
                              value={fromBankValue}
                              getOptionLabel={bankLabel}
                              renderOption={(props, option) => (
                                 <li {...props}>
                                    {option.bank_name} — {option.account_number}
                                 </li>
                              )}
                              onChange={(e, val) =>
                                 handleFormChange("from_account", val ? val.id : "")
                              }
                              renderInput={(params) => (
                                 <TextField
                                    {...params}
                                    label="From Bank"
                                    size="small"
                                    fullWidth
                                 />
                              )}
                              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                           />
                        </Grid>

                        <Grid item xs={12} md={3}>
                           <Autocomplete
                              options={toBankOptions || []}
                              value={toBankValue}
                              getOptionLabel={bankLabel}
                              renderOption={(props, option) => (
                                 <li {...props}>
                                    {option.bank_name} — {option.account_number}
                                 </li>
                              )}
                              onChange={(e, val) =>
                                 handleFormChange("to_account", val ? val.id : "")
                              }
                              renderInput={(params) => (
                                 <TextField
                                    {...params}
                                    label="To Bank"
                                    size="small"
                                    fullWidth
                                 />
                              )}
                              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              label="Cheque No"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.cheque_no}
                              onChange={(e) =>
                                 handleFormChange("cheque_no", e.target.value)
                              }
                              onWheel={(e) => e.target.blur()}
                              inputProps={{ min: 0, step: "0.01" }}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              label="Transfer Amt"
                              type="number"
                              size="small"
                              fullWidth
                              value={formData.cash_amount}
                              onChange={(e) =>
                                 handleFormChange("cash_amount", e.target.value)
                              }
                              onWheel={(e) => e.target.blur()}
                              inputProps={{ min: 0, step: "0.01" }}
                           />
                        </Grid>
                     </>
                  )}
               </Grid>

               <Divider sx={{ my: 3 }} />

               <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                     <TextField
                        label="Narration"
                        size="small"
                        fullWidth
                        multiline
                        value={formData.narration}
                        onChange={(e) => handleFormChange("narration", e.target.value)}
                     />
                  </Grid>
               </Grid>
            </Box>
         </Box>
      </>
   );
};

export default ContraVoucherForm;